import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button, CircularProgress, TextField } from "@mui/material";
import {
  KahunPrimaryTypography,
  KahunSecondaryTypography,
} from "fe-web-components/styles/typography";
import {
  authenticateUser,
  authenticateUserGoogle,
  confirmSignUp,
  getResendConfirmationCodeFn,
  SIGN_IN_STATUS,
} from "user-management/src/cognito/kahunUserAuth";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { SupportedLanguages } from "@kahunmedical/common-configuration/dist/configuration/supportedLanguages";
import GoogleButton from "react-google-button";
//region [[ Styles ]]

//endregion [[ Styles ]]

//region [[ Props ]]

export enum KAHUN_PLATFORM {
  UNKNOWN = "",
  AGENT = "Agent",
  P1st = "Patient",
  ClinicManage = "Standalone clinic",
  Extension = "Chrome extension",
}

export interface LoginFormProps {
  onSignIn: (email?: string) => void;
  onError?: (error: any) => void;
  disabled?: boolean;
  keepLoading?: boolean;
  onLoading?: () => void;
  onLoadingDone?: () => void;
  platform: KAHUN_PLATFORM;
  clinic?: string;
  locale?: string;
  useSessionStorage?: boolean;
  socialLoginRedirect?: string;
  enableGoogleLogin?: boolean;
}
//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const LoginForm = ({
  onLoading,
  onLoadingDone,
  keepLoading,
  onSignIn,
  onError,
  clinic = null,
  locale = SupportedLanguages.English_US,
  platform = KAHUN_PLATFORM.UNKNOWN,
  socialLoginRedirect, //since google login redirects to the root, we need this WA
  enableGoogleLogin = true,
}: LoginFormProps) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [verificationCodeFn, setVerificationCodeFn] = useState(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [loading, setLoadingState] = useState(false);
  const passwordRef = useRef<HTMLInputElement>();
  /* Sync controlled props with sate */
  useEffect(() => {
    setLoadingState(keepLoading);
  }, [keepLoading, setLoadingState]);

  /* Keep loading state synced with parent if needed */
  const setLoading = useCallback(
    (state: boolean) => {
      if (state && onLoading) return onLoading();
      if (!state && onLoadingDone) return onLoadingDone();
      // Let parent update the state if we are controlled
      if (keepLoading || keepLoading === false) return;
      setLoadingState(state);
    },
    [onLoading, onLoadingDone],
  );

  const handleSignIn = useCallback(
    (e) => {
      setLoading(true);
      e.preventDefault();
      (async () => {
        try {
          await authenticateUser(email, password);
          evt({
            category: CATEGORY.USER_AUTHENTICATION,
            action: ACTION.LOG_IN,
            props: {
              platform,
              userId: email,
              clinic,
              locale,
            },
          });
          onSignIn && onSignIn(email);
        } catch (err) {
          if (
            err
              .toString()
              .includes(SIGN_IN_STATUS.CONFIRM_SIGN_IN_MISSING.toString()) ||
            err.toString().includes("User is not confirmed")
          ) {
            onError(
              "User exists, but not verified. Use the verification code sent to your email",
            );
            const verificationFn = getResendConfirmationCodeFn(email);
            setVerificationCodeFn(verificationFn);
            (await verificationFn)();
          } else {
            onError(err);
          }
        } finally {
          setLoading(false);
        }
      })();
    },
    [email, password, onSignIn, onError],
  );

  const handleEmailChange = useCallback((e) => setEmail(e.target.value), [
    setEmail,
  ]);

  const handlePasswordChange = useCallback((e) => setPassword(e.target.value), [
    setPassword,
  ]);

  const handleVerify = useCallback(
    (e) => {
      e.preventDefault();
      (async () => {
        try {
          await confirmSignUp(email, verificationCode);
          evt({
            category: CATEGORY.USER_AUTHENTICATION,
            action: ACTION.SIGN_UP,
            props: {
              platform,
              userId: email,
              clinic,
              locale,
            },
          });
          setVerificationCodeFn(null);
          handleSignIn(e);
        } catch (e) {
          onError(e["message"] || e);
        }
      })();
    },
    [handleSignIn, verificationCode, email],
  );

  const handleVerificationCode = useCallback(
    (e) => setVerificationCode(e.target.value),
    [setVerificationCode],
  );

  const handleEnterInEmail = useCallback((e) => {
    if ((e.key === "Enter" || e.Key === "F12") && passwordRef.current) {
      console.log(e);
      passwordRef.current.focus();
    }
  }, []);

  const handleEnterInPassword = useCallback(
    (e) => {
      if ((e.key === "Enter" || e.Key === "F12") && email && password) {
        handleSignIn(e);
      }
    },
    [handleSignIn, email, password],
  );

  return (
    <>
      <form>
        <TextField
          className={"email-input"}
          name="username"
          margin="normal"
          variant="outlined"
          label="Email Address"
          type="email"
          autoComplete="username"
          fullWidth
          required
          onChange={handleEmailChange}
          onKeyDown={handleEnterInEmail}
        />
        <TextField
          className={"password-input"}
          ref={passwordRef}
          name="password"
          margin="normal"
          variant="outlined"
          label="Password"
          type="password"
          autoComplete="password"
          fullWidth
          required
          onChange={handlePasswordChange}
          onKeyDown={handleEnterInPassword}
        />
        <Button
          variant="contained"
          fullWidth
          color="primary"
          type="button"
          onClick={handleSignIn}
          disabled={loading}
          className="submit-button"
        >
          {loading ? <CircularProgress size={20} /> : "Log In"}
        </Button>
        {enableGoogleLogin && (
          <>
            <span>Or: </span>
            <GoogleButton
              onClick={async () =>
                await authenticateUserGoogle(socialLoginRedirect)
              }
            />
          </>
        )}
      </form>
      {verificationCodeFn && (
        <div style={{ marginTop: 10 }}>
          <form>
            <KahunPrimaryTypography>
              Enter the verification code you received in your email.
            </KahunPrimaryTypography>
            <KahunSecondaryTypography>
              (If you don't see the email, check the spam folder for mail from
              info@kahun.com)
            </KahunSecondaryTypography>
            <TextField
              fullWidth
              required
              margin="normal"
              variant="outlined"
              label="Verification Code"
              onChange={handleVerificationCode}
            />
            <Button
              variant="contained"
              fullWidth
              color={"primary"}
              onClick={handleVerify}
            >
              Verify
            </Button>
            <Button color={"primary"} onClick={verificationCodeFn}>
              Resend Code
            </Button>
          </form>
        </div>
      )}
    </>
  );
};
